interface ActiveOscillator {
  oscs: OscillatorNode[]
  gain: GainNode
}

type AudioContextConstructor = typeof AudioContext

interface WindowWithWebkitAudio extends Window {
  webkitAudioContext?: AudioContextConstructor
}

/**
 * Zungen je Ton: eine rein, zwei leicht schwebend (Musette). In Cent statt Hz – eine feste
 * Hz-Schwebung wäre im Bass (~100 Hz) eine Verstimmung um einen Viertelton.
 */
const REED_DETUNE_CENTS = [0, 8, -8] as const
/** Die Stimmzunge erzeugt Luftpulse statt eines Sägezahns; schmaler Puls = nasaler, zungiger. */
const PULSE_DUTY = 0.3
const PULSE_HARMONICS = 40
/** Die Zungenkammer dämpft die hohen Obertöne – ohne Filter klingt es nach Synthesizer. */
const CHAMBER_CUTOFF_HZ = 2800

/** Pulswelle als Fourier-Reihe: a_n = 2/(nπ) · sin(nπ·d). */
function createReedWave(context: AudioContext): PeriodicWave {
  const real = new Float32Array(PULSE_HARMONICS + 1)
  for (let n = 1; n <= PULSE_HARMONICS; n += 1) real[n] = (2 / (n * Math.PI)) * Math.sin(n * Math.PI * PULSE_DUTY)
  return context.createPeriodicWave(real, new Float32Array(PULSE_HARMONICS + 1))
}

/**
 * Audio engine for the Steirische Harmonika using Web Audio API.
 * Je Ton drei Zungen (`REED_DETUNE_CENTS`) durch einen Kammer-Tiefpass; ein Kompressor am Ausgang
 * fängt Akkorde ab, die sonst übersteuerten.
 */
export class AudioEngine {
  private ctx: AudioContext | null = null
  private masterGain: GainNode | null = null
  private reedWave: PeriodicWave | null = null
  private activeOscillators: Map<string, ActiveOscillator> = new Map()

  constructor() {
    try {
      const AudioContextClass = window.AudioContext ?? (window as WindowWithWebkitAudio).webkitAudioContext
      if (!AudioContextClass) return

      const context = new AudioContextClass()
      const masterGain = context.createGain()
      masterGain.gain.value = 0.4
      const limiter = context.createDynamicsCompressor()
      masterGain.connect(limiter)
      limiter.connect(context.destination)

      this.ctx = context
      this.masterGain = masterGain
      this.reedWave = createReedWave(context)
    } catch (error) {
      console.error('AudioContext not supported', error)
    }
  }

  resume(): void {
    if (this.ctx?.state === 'suspended') {
      void this.ctx.resume()
    }
  }

  playNote(frequency: number, id: string): void {
    if (!this.ctx || !this.masterGain || !this.reedWave) return

    this.resume()
    this.stopNote(id)

    const now = this.ctx.currentTime
    const level = 1 / REED_DETUNE_CENTS.length
    const noteGain = this.ctx.createGain()
    noteGain.connect(this.masterGain)
    noteGain.gain.setValueAtTime(0, now)
    noteGain.gain.linearRampToValueAtTime(level, now + 0.04)
    noteGain.gain.linearRampToValueAtTime(level * 0.8, now + 0.2)

    const chamber = this.ctx.createBiquadFilter()
    chamber.type = 'lowpass'
    chamber.frequency.value = CHAMBER_CUTOFF_HZ
    chamber.connect(noteGain)

    const oscillators = REED_DETUNE_CENTS.map((cents) => {
      const oscillator = this.ctx!.createOscillator()
      oscillator.setPeriodicWave(this.reedWave!)
      oscillator.frequency.value = frequency
      oscillator.detune.value = cents
      oscillator.connect(chamber)
      oscillator.start(now)
      return oscillator
    })

    this.activeOscillators.set(id, {oscs: oscillators, gain: noteGain})
  }

  stopNote(id: string): void {
    if (!this.ctx) return

    const active = this.activeOscillators.get(id)
    if (!active) return

    const releaseTime = 0.1

    try {
      active.gain.gain.cancelScheduledValues(this.ctx.currentTime)
      active.gain.gain.setValueAtTime(active.gain.gain.value, this.ctx.currentTime)
      active.gain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + releaseTime)

      const stopTime = this.ctx.currentTime + releaseTime
      active.oscs.forEach((oscillator) => oscillator.stop(stopTime))
    } catch {
      // Oscillators can already be stopped by browser audio scheduling.
    }

    this.activeOscillators.delete(id)
  }
}
